import {
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
  TabList,
  Tab,
} from '@fluentui/react-components';
import type { EChartsOption } from 'echarts';
import { useMemo, useState } from 'react';
import { Chart } from '../common/charts/Chart';
import { useChartColors } from '../common/charts/chartTheme';
import { LoadingState } from '../common/LoadingState';
import type { TrendData } from '../../types';

const useStyles = makeStyles({
  card: {
    minWidth: '400px',
    flex: '2 1 400px',
  },
  content: {
    padding: tokens.spacingHorizontalM,
  },
  chartContainer: {
    height: '280px',
    marginTop: tokens.spacingVerticalM,
  },
  tabs: {
    marginBottom: tokens.spacingVerticalS,
  },
  legend: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
});

interface TrendChartProps {
  data: TrendData | null;
  isLoading?: boolean;
}

type TrendType = 'volume' | 'jitter' | 'latency' | 'packetLoss' | 'badCalls';

// Fixed order used both to render the tab strip and to index into
// `useChartColors().categorical(n)` below, so each metric keeps a stable,
// visually distinct color across tab switches without any color literal
// living outside `chartTheme.ts`.
const METRIC_ORDER: TrendType[] = ['volume', 'jitter', 'latency', 'packetLoss', 'badCalls'];

const trendConfig = {
  volume: {
    label: 'Daily Calls',
    tabLabel: 'Volume',
    dataKey: 'call_volume_trend',
    valueKey: 'value',
    unit: ' calls',
    description: 'Total number of calls per day',
  },
  jitter: {
    label: 'Avg Jitter (ms)',
    tabLabel: 'Jitter',
    dataKey: 'quality_trend',
    valueKey: 'value',
    unit: ' ms',
    description: 'Average jitter in milliseconds. Microsoft thresholds: Warning >24ms, Poor >30ms',
  },
  latency: {
    label: 'Avg Latency (ms)',
    tabLabel: 'Latency',
    dataKey: 'quality_trend',
    valueKey: 'latency',
    unit: ' ms',
    description: 'Average one-way latency in milliseconds. Microsoft thresholds: Warning >160ms, Poor >200ms',
  },
  packetLoss: {
    label: 'Avg Packet Loss (%)',
    tabLabel: 'Pkt Loss',
    dataKey: 'quality_trend',
    valueKey: 'packet_loss',
    unit: '%',
    description: 'Average packet loss percentage. Microsoft thresholds: Warning >4%, Poor >5%',
  },
  badCalls: {
    label: 'Bad Call %',
    tabLabel: 'Bad Calls',
    dataKey: 'bad_call_trend',
    valueKey: 'value',
    unit: '%',
    description: 'Percentage of calls exceeding poor thresholds (jitter >30ms, latency >200ms, or packet loss >5%)',
  },
};

export function TrendChart({ data, isLoading }: TrendChartProps) {
  const styles = useStyles();
  const [selectedTrend, setSelectedTrend] = useState<TrendType>('volume');
  const colors = useChartColors();
  const palette = useMemo(() => colors.categorical(METRIC_ORDER.length), [colors]);
  const seriesColor = palette[METRIC_ORDER.indexOf(selectedTrend)];

  const config = trendConfig[selectedTrend];
  const isPercent = selectedTrend === 'badCalls' || selectedTrend === 'packetLoss';

  const chartData = useMemo(
    () =>
      data
        ? (data[config.dataKey as keyof TrendData] as Array<{
            date: string;
            value: number;
            latency?: number;
            packet_loss?: number;
          }>)
        : [],
    [data, config.dataKey],
  );

  const formattedData = useMemo(
    () =>
      chartData.map((item) => ({
        date: new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        }),
        // Map the correct value based on which metric is selected
        displayValue: config.valueKey === 'latency'
          ? (item.latency ?? 0)
          : config.valueKey === 'packet_loss'
            ? (item.packet_loss ?? 0)
            : item.value,
      })),
    [chartData, config.valueKey],
  );

  const option: EChartsOption = useMemo(
    () => ({
      grid: { left: 48, right: 16, top: 16, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) =>
          typeof v === 'number' ? `${v.toFixed(1)}${config.unit}` : `${v}`,
      },
      xAxis: {
        type: 'category',
        data: formattedData.map((item) => item.date),
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => (isPercent ? `${value}%` : String(value)),
        },
      },
      series: [
        {
          name: config.label,
          type: 'line',
          smooth: true,
          showSymbol: true,
          symbolSize: 6,
          data: formattedData.map((item) => item.displayValue),
          lineStyle: { width: 2, color: seriesColor },
          itemStyle: { color: seriesColor },
        },
      ],
    }),
    [formattedData, config.label, config.unit, isPercent, seriesColor],
  );

  if (isLoading || !data) {
    return (
      <Card className={styles.card}>
        <CardHeader header={<Text weight="semibold">Trends</Text>} />
        <div className={styles.content}>
          <LoadingState variant="panel" layout="chart" height={280} />
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardHeader header={<Text weight="semibold">Trends</Text>} />
      <div className={styles.content}>
        <TabList
          className={styles.tabs}
          selectedValue={selectedTrend}
          onTabSelect={(_, data) => setSelectedTrend(data.value as TrendType)}
        >
          <Tab value="volume">{trendConfig.volume.tabLabel}</Tab>
          <Tab value="jitter">{trendConfig.jitter.tabLabel}</Tab>
          <Tab value="latency">{trendConfig.latency.tabLabel}</Tab>
          <Tab value="packetLoss">{trendConfig.packetLoss.tabLabel}</Tab>
          <Tab value="badCalls">{trendConfig.badCalls.tabLabel}</Tab>
        </TabList>

        <Text className={styles.legend}>{config.description}</Text>

        <div className={styles.chartContainer}>
          <Chart
            option={option}
            height="100%"
            ariaLabel={`${config.label} trend over time`}
          />
        </div>
      </div>
    </Card>
  );
}
